import { isInternalAdminOnlyFolderName } from '@canopy/shared';
import { useReorderTree } from '../api/reorder.js';
import './insights-polish.css';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  useDashboards,
  useDeleteDashboard,
  useStandardOverview,
  useUpdateDashboardById,
  type StandardCustomerBoards,
} from '../api/dashboards.js';
import { activeScopeCustomer } from '../auth/active-scope.js';
import { useReports, useUpdateReport, useCloneReport, useDeleteReport } from '../api/reports.js';
import {
  useFolders,
  useCreateFolder,
  useRenameFolder,
  useDeleteFolder,
  useMoveFolder,
  type Folder,
} from '../api/folders.js';
import { DashboardFolderTree } from './dashboards/DashboardFolderTree.js';
import { ReportsFolderTree } from './reports-instances/ReportsFolderTree.js';
import { ConsumerReportsList, buildDefinitions } from './reports-instances/ConsumerReportsList.js';
import {
  InsightsLibrary,
  InsightsViewerHeader,
  descendantFolderIds,
  type LibrarySelection,
  type LibraryItem,
} from './InsightsLibrary.js';
import { DashboardViewer } from './dashboards/DashboardViewer.js';
import { ManageAccessDialog } from './dashboards/ManageAccessDialog.js';
import { ReportViewer } from './reports/ReportViewer.js';
import { SubscriptionsManager } from './subscriptions/SubscriptionsManager.js';
import { SftpCredentialsManager } from './subscriptions/SftpCredentialsManager.js';
import { DataStatusPanel } from './DataStatusPanel.js';
import { ReportsCategoryList, DashboardsCategoryList } from './CategoryList.js';
import { useListSidebar, useListSidebarWidth } from '../shell/useAppChrome.js';
import {
  useCanManageDashboardAccess,
  useInsightsSections,
  useIsAdmin,
  useIsInternalAdmin,
  useMyRoles,
} from '../api/me.js';
import { useAllSchedules } from '../api/schedules.js';
import { ChevronRight, NavigationPanelIcon, SearchIcon } from '../components/icons.js';
import { PromptDialog } from '../components/dialogs/PromptDialog.js';
import { ConfirmDialog } from '../components/dialogs/ConfirmDialog.js';
import { MoveDialog } from '../components/dialogs/MoveDialog.js';

/**
 * Embed-mode shell for the Insights surface.
 *
 * Loaded by InsightsPage when the SPA detects `?embed=1` — that's the
 * Canopy portal Insights module. This view is intentionally
 * separate from the standalone-Sage InsightsPage:
 *
 *   - Standalone Sage authors live in InsightsPage, with the full
 *     Designer + tabs + new/rename/move/delete affordances.
 *   - Embedded consumers live here, with only the folder tree to
 *     browse and a clean viewer to look at.
 *
 * The sidebar is one sectioned column (no tab strip): a Reports
 * section and a Dashboards section — each a collapsible header with
 * an object-count pill and its folder tree inline — plus, for admins
 * only, an Admin section pinned to the bottom that expands to the
 * admin surfaces (Subscriptions, with its own live count). Both
 * content sections use the existing tenant-scoped APIs Sage already
 * exposes; this view doesn't introduce a new data path. Each section
 * keeps its own selection so browsing one doesn't lose the other.
 *
 * Presentations are deliberately left for a follow-up — Canopy Hub
 * has only 2 today and Sage's PresentationsPage carries a different
 * editor model that needs a viewer pass of its own.
 */
/** Which surface the right-hand pane is showing. The `-list` views are the
 *  category tables opened by clicking a section header; the bare views are
 *  the object viewers opened by clicking an object (tree or table row). */
type View =
  | 'library'
  | 'dashboards'
  | 'reports'
  | 'subscriptions'
  | 'sftp-credentials'
  | 'data-status'
  | 'reports-list'
  | 'dashboards-list';

const NOOP = (): void => {
  /* viewer mode — authoring callbacks are inert */
};

/** CSAGE-586: URL parameter naming the customer a standard board is viewed as. */
const AS_CUSTOMER_PARAM = 'asCustomer';

export function EmbedInsightsView(): JSX.Element {
  const navigate = useNavigate();
  const { id: urlId } = useParams<{ id: string }>();

  // Start with the combined library; a dashboard deep link opens its viewer.
  const [librarySelection, setLibrarySelection] = useState<LibrarySelection>({ kind: 'All' });
  const [view, setView] = useState<View>(urlId ? 'dashboards' : 'library');
  const [selectedDashboard, setSelectedDashboard] = useState<string | null>(urlId ?? null);
  // CSAGE-586: a standard board opened from a customer's "Standard" group is
  // shown as that customer sees it. The customer rides in the URL
  // (`?asCustomer=`), so Back, reload and deep links keep or drop it with the
  // board rather than a stale piece of state.
  const location = useLocation();
  const asCustomerParam = new URLSearchParams(location.search).get(AS_CUSTOMER_PARAM);
  // Folder whose contents are shown in the dashboards listing; null = all
  // dashboards. Set by clicking a folder row, cleared by the Dashboards header.
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  // CSAGE-353: set when the user chose "Subscribe…" on a My Reports row, so the
  // viewer opens straight into its Subscriptions modal. Cleared on any ordinary
  // selection so it can't re-fire when they next open the same report.
  const [subscribeFor, setSubscribeFor] = useState<string | null>(null);

  // Any *Admin role unlocks the Admin section. Roles come from
  // GET /me/tenants (server-resolved ctx.roles), which works for the
  // portal/HMAC session token — parseDevToken only understands dev
  // tokens and would hide the section from real portal admins. The list
  // itself is scope-filtered server-side, so a customer admin only ever
  // sees their own subtree's subscriptions.
  const isAdmin = useIsAdmin();

  // Subscription count for the Admin section pill. The endpoint is
  // admin-gated, so the fetch only runs for admins (no 403 polling for
  // consumers). Scope filtering is server-side, so the count matches
  // exactly what the Subscriptions surface will show this admin.
  const schedulesQ = useAllSchedules(isAdmin);
  const subscriptionCount = schedulesQ.data?.length;

  // Sidebar section state: Reports, Dashboards and Admin all start collapsed so
  // the column opens as a tidy list of section headers; the user expands the
  // one they want.
  const [reportsOpen, setReportsOpen] = useState(false);
  const [dashboardsOpen, setDashboardsOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);

  // Folder tree state — one expanded set per section so toggles don't
  // leak across surfaces.
  const [search, setSearch] = useState('');
  const [dashExpanded, setDashExpanded] = useState<Set<string>>(new Set());
  const [reportExpanded, setReportExpanded] = useState<Set<string>>(new Set());
  function toggleDash(id: string): void {
    setDashExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleReport(id: string): void {
    setReportExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // CSAGE-556: show only the sections the session's roles allow, and do not
  // fetch the other one (the API would refuse it). A user with neither gets
  // the no-access state below. Nothing renders until the roles are known:
  // while /me/tenants loads, `sections` reports both as allowed, which
  // flashed the Reports section at dashboards-only users on every load.
  const sections = useInsightsSections();
  const showDashboards = sections.ready && sections.dashboards;
  const showReports = sections.ready && sections.reports;
  const dashboardsQ = useDashboards({ enabled: showDashboards });
  const reportsQ = useReports(undefined, { enabled: showReports });
  const dashFoldersQ = useFolders('dashboards', { enabled: showDashboards });
  const reportFoldersQ = useFolders('reports', { enabled: showReports });

  const listSidebar = useListSidebar('insights-embed');
  const listSidebarWidth = useListSidebarWidth('insights-embed');
  const collapsed = listSidebar.mode === 'collapsed';

  // Land on Insights with the list OPEN and nothing rendering. We do NOT
  // auto-open the first report/dashboard: a consumer should choose what to
  // view. Auto-loading the first item made Sage appear to "spin and fail"
  // when that arbitrary first item couldn't render. The empty state guides
  // the user to pick from the list.
  //
  // Force the list expanded on a fresh landing so the user can actually
  // pick — earlier builds persisted a "collapsed" preference, so without
  // this a returning user would land on the empty state with no visible
  // list. A deep link to a specific item (urlId) keeps the stored
  // preference so the viewer can claim the width.
  useEffect(() => {
    if (!urlId) listSidebar.set('expanded');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep dashboard selection in sync with the URL so deep links (and
  // drill-through from other surfaces) still work.
  useEffect(() => {
    if (urlId) {
      setSelectedDashboard(urlId);
      setView('dashboards');
    } else {
      setView('library');
    }
  }, [urlId]); // eslint-disable-line react-hooks/exhaustive-deps

  function selectDashboard(id: string | null, asCustomer?: string): void {
    setSelectedDashboard(id);
    setView('dashboards');
    const search = new URLSearchParams(window.location.search);
    if (asCustomer) search.set(AS_CUSTOMER_PARAM, asCustomer);
    else search.delete(AS_CUSTOMER_PARAM);
    const query = search.toString() ? `?${search.toString()}` : '';
    navigate(id ? `/insights/${id}${query}` : `/insights${query}`);
  }

  // Clicking a folder in the tree shows that folder's contents in the main
  // pane. Passing null falls back to the full dashboards listing.
  function selectFolder(id: string | null): void {
    setSelectedFolderId(id);
    setDashboardsOpen(true);
    const ids = id ? descendantFolderIds(id, visibleDashFolders) : null;
    setLibrarySelection({
      kind: 'Dashboard',
      ...(id
        ? {
            folder: visibleDashFolders.find((f) => f.id === id)?.name ?? 'Folder',
            itemIds: visibleDashboards
              .filter((d) => d.folderId && ids?.has(d.folderId))
              .map((d) => d.id),
          }
        : {}),
    });
    setView('library');
  }

  function selectReport(id: string): void {
    setSelectedReport(id);
    setSubscribeFor(null); // an ordinary open must not re-trigger the modal
    setView('reports');
  }

  /** CSAGE-353: "Subscribe…" from a My Reports row — open the report AND its
   *  Subscriptions modal, so the kebab action finishes what it started. */
  function subscribeToReport(id: string): void {
    setSelectedReport(id);
    setSubscribeFor(id);
    setView('reports');
  }

  // CSAGE-439 — TEMPORARY rollout gate, scoped to the ANALYTICS FOLDER only.
  // The top-level "Analytics" folder holds the Sisense-replacement estate,
  // which stays internal-Canopy-admin-only until it passes review. Customer
  // boards (Spencer/Subway/Ricoh folders) are NOT gated: those customers
  // are already live on dashboards in production, and the first cut of this
  // gate hiding the whole section locked them out (Spencer, 2026-09-10).
  // Non-internal users don't see the Analytics folder, its subfolders, or
  // its dashboards — tree, counts, category list, or deep link (which
  // falls back to the pick-one empty state). Remove when Analytics goes GA.
  // CSAGE-513: the top-level "Templates" folder uses the same gate. The
  // folder names come from INTERNAL_ADMIN_ONLY_FOLDER_NAMES (@canopy/shared),
  // which the API uses for the same server-side gate.
  const isInternalAdmin = useIsInternalAdmin();
  // CSAGE-586: from the org root an internal admin sees, under each customer's
  // folder, the standard boards that customer is served (they live in the
  // standard folder). With a customer selected the tree is already theirs.
  const atOrgRoot = isInternalAdmin && activeScopeCustomer() === null;
  // The route needs a dashboard admin role; `useIsInternalAdmin` also counts
  // report admins, who would only collect 403s.
  const { roles: myRoles } = useMyRoles();
  const standardOverview = useStandardOverview(
    atOrgRoot &&
      showDashboards &&
      myRoles.some((r) => r === 'DashboardAdmin' || r === 'TenantAdmin'),
  );
  const standardByCustomer = useMemo(() => {
    const data = standardOverview.data;
    if (!atOrgRoot || !data?.served) return undefined;
    return new Map(data.customers.map((c) => [c.customer.toLowerCase(), c]));
  }, [atOrgRoot, standardOverview.data]);
  function openStandardAsCustomer(id: string, entry: StandardCustomerBoards): void {
    selectDashboard(id, entry.customer);
  }
  // Only a customer this board is provisioned to: a hand-edited or stale URL
  // falls back to the plain view.
  const viewingAs = useMemo(() => {
    if (!asCustomerParam || !selectedDashboard || !standardByCustomer) return null;
    const entry = standardByCustomer.get(asCustomerParam.toLowerCase());
    if (!entry?.boards.some((b) => b.dashboardId === selectedDashboard)) return null;
    return { dashboardId: selectedDashboard, customer: entry.customer, scopePath: entry.scopePath };
  }, [asCustomerParam, selectedDashboard, standardByCustomer]);
  const gatedFolderIds = useMemo(() => {
    const gated = new Set<string>();
    if (isInternalAdmin) return gated;
    const folders = dashFoldersQ.data ?? [];
    for (const f of folders) {
      if (f.parentId == null && isInternalAdminOnlyFolderName(f.name)) gated.add(f.id);
    }
    let grew = true;
    while (grew) {
      grew = false;
      for (const f of folders) {
        if (f.parentId && gated.has(f.parentId) && !gated.has(f.id)) {
          gated.add(f.id);
          grew = true;
        }
      }
    }
    return gated;
  }, [isInternalAdmin, dashFoldersQ.data]);
  const visibleDashboards = useMemo(
    () => (dashboardsQ.data ?? []).filter((d) => !(d.folderId && gatedFolderIds.has(d.folderId))),
    [dashboardsQ.data, gatedFolderIds],
  );
  const visibleDashFolders = useMemo(
    () => (dashFoldersQ.data ?? []).filter((f) => !gatedFolderIds.has(f.id)),
    [dashFoldersQ.data, gatedFolderIds],
  );
  const selectionGated =
    selectedDashboard != null &&
    (dashboardsQ.data ?? []).some(
      (d) => d.id === selectedDashboard && d.folderId != null && gatedFolderIds.has(d.folderId),
    );

  const dashboardCount = visibleDashboards.length;

  // "New folder" lives on the Reports/Dashboards section headers (for
  // organizers), plus the tree's "New folder inside" menu. Both open one in-app
  // dialog — window.prompt is blocked in the cross-origin embed iframe, so a
  // native prompt silently does nothing. `canOrganize` mirrors the role gate
  // used for the in-tree organizing actions.
  const { roles } = useMyRoles();
  const canOrganize =
    roles.length === 0 ||
    roles.some(
      (r) =>
        r === 'ReportAuthor' ||
        r === 'ReportAdmin' ||
        r === 'DashboardAuthor' ||
        r === 'DashboardAdmin' ||
        r === 'TenantAdmin',
    );
  const createFolder = useCreateFolder();
  const [newFolder, setNewFolder] = useState<{
    type: 'dashboards' | 'reports';
    parent: Folder | null;
  } | null>(null);
  const requestNewFolder = (type: 'dashboards' | 'reports', parent: Folder | null): void =>
    setNewFolder({ type, parent });

  // The dashboards listing, scoped to the selected folder (its whole subtree)
  // when one is picked, else all dashboards. The heading becomes the folder's
  // name so the main pane reads as "what's in this folder".
  const folderListing = useMemo(() => {
    if (!selectedFolderId) {
      return {
        list: visibleDashboards,
        title: 'Dashboards',
        subtitle: 'All dashboards available to you. Select one to view it.',
      };
    }
    const ids = new Set<string>([selectedFolderId]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const f of visibleDashFolders) {
        if (f.parentId && ids.has(f.parentId) && !ids.has(f.id)) {
          ids.add(f.id);
          grew = true;
        }
      }
    }
    const folder = visibleDashFolders.find((f) => f.id === selectedFolderId);
    return {
      list: visibleDashboards.filter((d) => d.folderId != null && ids.has(d.folderId)),
      title: folder?.name ?? 'Dashboards',
      subtitle: folder
        ? `Dashboards in ${folder.name}. Select one to view it.`
        : 'Select one to view it.',
    };
  }, [selectedFolderId, visibleDashboards, visibleDashFolders]);
  // Reports section count must match the collapsed browse list the consumer
  // actually sees. That list groups by report *name* (buildDefinitions —
  // per-location + per-workgroup template variants that share a display name
  // collapse to one row). Counting distinct templateId here diverged from that
  // after CSAGE-192/7e96d53 (e.g. Mood Media showed 10 templates vs 7 names).
  // Subscription-target instances (hierarchy + scheduled) move to their own
  // "Scheduled Reports" folder (CSAGE-272), so exclude them from the catalog
  // count to keep the badge aligned with the main list.
  const reportCount = buildDefinitions(
    (reportsQ.data ?? []).filter((r) => !(r.visibility === 'hierarchy' && r.scheduleId != null)),
  ).length;

  // Templates map for the reports tree tooltip — empty in embed, the
  // tree falls back to the report name (which is what consumers
  // recognize anyway). Fetching the templates list is an extra
  // request for a tooltip we don't need on first launch.

  const libraryItems: LibraryItem[] = [
    ...visibleDashboards.map((d) => ({
      id: d.id,
      name: d.name,
      kind: 'Dashboard' as const,
      folder: visibleDashFolders.find((f) => f.id === d.folderId)?.name ?? 'General',
    })),
    ...buildDefinitions(
      (reportsQ.data ?? []).filter(
        (r) =>
          r.visibility !== 'personal' && !(r.visibility === 'hierarchy' && r.scheduleId != null),
      ),
    ).map((d) => ({
      id: d.representativeId,
      name: d.label,
      kind: 'Report' as const,
      folder: 'Shared reports',
    })),
    ...(reportsQ.data ?? [])
      .filter((r) => r.visibility === 'hierarchy' && r.scheduleId != null)
      .map((r) => ({
        id: r.id,
        name: r.name,
        kind: 'Report' as const,
        folder: 'Scheduled reports',
      })),
    ...(reportsQ.data ?? [])
      .filter((r) => r.visibility === 'personal')
      .map((r) => ({ id: r.id, name: r.name, kind: 'Report' as const, folder: 'My reports' })),
  ];
  const [viewerRevision, setViewerRevision] = useState(0);
  // The open dashboard's "download as PDF" action, handed up by DashboardViewer
  // once it holds a grant, for the PDF button beside Refresh. Stored through an
  // updater because a bare function passed to a state setter is CALLED.
  const [pdfExport, setPdfExport] = useState<(() => void) | null>(null);
  const onPdfExportChange = useCallback((fn: (() => void) | null) => setPdfExport(() => fn), []);
  // CSAGE-557: "Manage access" for the open dashboard, offered only to those
  // the API lets change its audience.
  const canManageAccess = useCanManageDashboardAccess();
  const [accessOpenFor, setAccessOpenFor] = useState<string | null>(null);
  const closeAccess = useCallback(() => setAccessOpenFor(null), []);
  function openLibrary(): void {
    setLibrarySelection({ kind: 'All' });
    setView('library');
    navigate(`/insights${window.location.search}`);
  }
  const viewerName =
    view === 'dashboards'
      ? (visibleDashboards.find((d) => d.id === selectedDashboard)?.name ?? 'Dashboard')
      : ((reportsQ.data ?? []).find((r) => r.id === selectedReport)?.name ?? 'Report');

  if (!sections.reports && !sections.dashboards) {
    return (
      <div className="empty-state" style={{ border: 'none', padding: 'var(--space-6)' }}>
        <h3>No access to Insights</h3>
        <p className="text-dim">
          Your role does not include Insights reports or dashboards. Ask your administrator for
          access.
        </p>
      </div>
    );
  }

  return (
    <div
      className="split-layout insights-layout insights-experience"
      data-sidebar={listSidebar.mode}
      style={
        !collapsed
          ? ({ '--list-sidebar-width': `${listSidebarWidth.width}px` } as CSSProperties)
          : undefined
      }
    >
      <aside className="split-sidebar embed-sidebar">
        {/* When collapsed, the body (incl. the toolbar) is hidden, so the
            expand control lives in its own slim heading. When expanded, it
            moves inline to the right of the search box (below) to save a row. */}
        {collapsed && (
          <div className="insights-navigation-heading">
            <button
              type="button"
              className="embed-collapse-toggle"
              onClick={listSidebar.toggle}
              aria-expanded={false}
              title="Show navigation"
              aria-label="Show navigation"
            >
              <NavigationPanelIcon size={18} collapsed />
            </button>
          </div>
        )}
        <div className="embed-sidebar-body">
          <div className="split-sidebar-toolbar">
            <div className="split-sidebar-search">
              <SearchIcon />
              <input
                type="search"
                placeholder="Search reports and dashboards…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search"
              />
              {search && (
                <button
                  type="button"
                  className="btn-icon split-sidebar-clear"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            {!collapsed && (
              <button
                type="button"
                className="embed-collapse-toggle embed-collapse-toggle--inline"
                onClick={listSidebar.toggle}
                aria-expanded
                title="Hide navigation"
                aria-label="Hide navigation"
              >
                <NavigationPanelIcon size={18} collapsed={false} />
              </button>
            )}
          </div>

          {/* Content sections: header (label + object count) with the
              folder tree inline underneath. */}
          <div className="embed-sections">
            {showReports && (
              <section className="embed-section">
                <SectionHeader
                  label="Reports"
                  count={reportCount}
                  open={reportsOpen}
                  active={
                    view === 'reports-list' ||
                    view === 'reports' ||
                    (view === 'library' && librarySelection.kind === 'Report')
                  }
                  onToggle={() => setReportsOpen((v) => !v)}
                  onOpenList={() => {
                    setReportsOpen(true);
                    setLibrarySelection({ kind: 'Report' });
                    setView('library');
                  }}
                  /* No new-folder on Reports: the embed's reports view
                   (ConsumerReportsList) is grouped by report definition, not by
                   custom folders, so a created folder would never display.
                   Dashboards keep folders. */
                />
                {reportsOpen && (
                  <div className="embed-section-body">
                    <ReportsTabBody
                      onSelectSection={(folder, itemIds) => {
                        setLibrarySelection({ kind: 'Report', folder, itemIds });
                        setView('library');
                      }}
                      reportsQ={reportsQ}
                      foldersQ={reportFoldersQ}
                      selected={view === 'reports' ? selectedReport : null}
                      onSelect={selectReport}
                      onSubscribe={subscribeToReport}
                      expanded={reportExpanded}
                      onToggle={toggleReport}
                      search={search}
                      templatesById={undefined}
                      consumerMode
                      onNewFolder={(parent) => requestNewFolder('reports', parent)}
                    />
                  </div>
                )}
              </section>
            )}

            {showDashboards && (
              <section className="embed-section">
                <SectionHeader
                  label="Dashboards"
                  count={dashboardCount}
                  open={dashboardsOpen}
                  active={
                    view === 'dashboards-list' ||
                    view === 'dashboards' ||
                    (view === 'library' && librarySelection.kind === 'Dashboard')
                  }
                  onToggle={() => setDashboardsOpen((v) => !v)}
                  onOpenList={() => {
                    setSelectedFolderId(null);
                    setDashboardsOpen(true);
                    setLibrarySelection({ kind: 'Dashboard' });
                    setView('library');
                  }}
                  onNewFolder={canOrganize ? () => requestNewFolder('dashboards', null) : undefined}
                />
                {dashboardsOpen && (
                  <div className="embed-section-body">
                    <DashboardsTabBody
                      dashboardsQ={
                        { ...dashboardsQ, data: visibleDashboards } as typeof dashboardsQ
                      }
                      foldersQ={
                        { ...dashFoldersQ, data: visibleDashFolders } as typeof dashFoldersQ
                      }
                      selected={view === 'dashboards' && !viewingAs ? selectedDashboard : null}
                      onSelect={selectDashboard}
                      standardByCustomer={standardByCustomer}
                      onOpenStandardAsCustomer={openStandardAsCustomer}
                      selectedStandard={view === 'dashboards' ? viewingAs : null}
                      onSelectFolder={selectFolder}
                      expanded={dashExpanded}
                      onToggle={toggleDash}
                      search={search}
                      onNewFolder={(parent) => requestNewFolder('dashboards', parent)}
                      onManageAccess={canManageAccess ? setAccessOpenFor : undefined}
                    />
                  </div>
                )}
              </section>
            )}
          </div>

          {/* Data status — internal Canopy admins only. Still scope-aware:
              the numbers reflect wherever the admin is in the hierarchy. */}
          {isInternalAdmin && (
            <div className="embed-admin">
              <button
                type="button"
                className={
                  'embed-admin-item' + (view === 'data-status' ? ' embed-admin-item--active' : '')
                }
                aria-current={view === 'data-status' ? 'page' : undefined}
                onClick={() => setView('data-status')}
              >
                <span>Data status</span>
              </button>
            </div>
          )}

          {/* Admin — pinned to the bottom of the column, admins only.
              Expands to the admin surfaces; Subscriptions lives here. */}
          {isAdmin && (
            <div className="embed-admin">
              <SectionHeader
                label="Admin"
                open={adminOpen}
                onToggle={() => setAdminOpen((v) => !v)}
              />
              {adminOpen && (
                <button
                  type="button"
                  className={
                    'embed-admin-item' +
                    (view === 'subscriptions' ? ' embed-admin-item--active' : '')
                  }
                  aria-current={view === 'subscriptions' ? 'page' : undefined}
                  onClick={() => setView('subscriptions')}
                >
                  <span>Subscriptions</span>
                  {subscriptionCount !== undefined && (
                    <span className="embed-tab-count">{subscriptionCount}</span>
                  )}
                </button>
              )}
              {adminOpen && (
                <button
                  type="button"
                  className={
                    'embed-admin-item' +
                    (view === 'sftp-credentials' ? ' embed-admin-item--active' : '')
                  }
                  aria-current={view === 'sftp-credentials' ? 'page' : undefined}
                  onClick={() => setView('sftp-credentials')}
                >
                  <span>SFTP credentials</span>
                </button>
              )}
              {/* The Sage admin console (cubes and the history backfill
                  queue, data sources, API keys, cutover parity). The portal
                  only ever loads /insights, so without this an internal
                  admin had no way to reach /admin in prod. Internal Canopy
                  admins only: these are platform surfaces, not customer
                  ones. App.tsx's EmbedAdminBar is the way back. */}
              {adminOpen && isInternalAdmin && (
                <button
                  type="button"
                  className="embed-admin-item"
                  onClick={() => navigate(`/admin${window.location.search}`)}
                >
                  <span>Sage settings</span>
                </button>
              )}
            </div>
          )}
        </div>
      </aside>

      {!collapsed && (
        <div
          className="split-sidebar-resizer"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize list panel"
          onDoubleClick={listSidebarWidth.reset}
          {...listSidebarWidth.bindResizer()}
          title="Drag to resize · double-click to reset"
        />
      )}

      <section>
        {view !== 'library' && view !== 'reports' && (
          <InsightsViewerHeader
            name={
              view === 'dashboards'
                ? viewerName
                : view === 'subscriptions'
                  ? 'Subscriptions'
                  : view === 'sftp-credentials'
                    ? 'SFTP credentials'
                    : view === 'data-status'
                      ? 'Data status'
                      : view === 'reports-list'
                        ? 'Reports'
                        : folderListing.title
            }
            kind={view === 'dashboards' ? '' : 'Insights'}
            onBack={openLibrary}
            onRefresh={view === 'dashboards' ? () => setViewerRevision((n) => n + 1) : undefined}
            onPdf={view === 'dashboards' && pdfExport ? pdfExport : undefined}
            onManageAccess={
              view === 'dashboards' && selectedDashboard && !selectionGated && canManageAccess
                ? () => setAccessOpenFor(selectedDashboard)
                : undefined
            }
          />
        )}
        <div hidden={view !== 'library'} className="insights-library-host">
          <InsightsLibrary
            selection={librarySelection}
            kinds={[
              ...(showDashboards ? (['Dashboard'] as const) : []),
              ...(showReports ? (['Report'] as const) : []),
            ]}
            items={libraryItems}
            loading={
              !sections.ready ||
              dashboardsQ.isLoading ||
              reportsQ.isLoading ||
              dashFoldersQ.isLoading
            }
            error={!!(dashboardsQ.error || reportsQ.error || dashFoldersQ.error)}
            onRetry={() => {
              void dashboardsQ.refetch();
              void reportsQ.refetch();
              void dashFoldersQ.refetch();
            }}
            onOpen={(item) =>
              item.kind === 'Dashboard' ? selectDashboard(item.id) : selectReport(item.id)
            }
          />
        </div>
        {view === 'library' ? null : view === 'subscriptions' ? (
          <SubscriptionsManager />
        ) : view === 'sftp-credentials' ? (
          <SftpCredentialsManager />
        ) : view === 'data-status' ? (
          <DataStatusPanel />
        ) : view === 'reports-list' ? (
          <ReportsCategoryList reports={reportsQ.data ?? []} onOpen={selectReport} />
        ) : view === 'dashboards-list' ? (
          <DashboardsCategoryList
            dashboards={folderListing.list}
            onOpen={selectDashboard}
            title={folderListing.title}
            subtitle={folderListing.subtitle}
          />
        ) : view === 'dashboards' ? (
          selectedDashboard && !selectionGated ? (
            <DashboardViewer
              key={`${selectedDashboard}:${viewingAs?.customer ?? ''}:${viewerRevision}`}
              dashboardId={selectedDashboard}
              onPdfExportChange={onPdfExportChange}
              asCustomer={viewingAs ?? undefined}
            />
          ) : (
            // CSAGE-472: no dashboard is open yet (nothing picked, or a deep
            // link into a gated one) — show the browsable dashboards listing
            // rather than a blank "pick one" pane, so the main pane always
            // offers the dashboards to choose from. Same list the Dashboards
            // section header opens.
            <DashboardsCategoryList dashboards={visibleDashboards} onOpen={selectDashboard} />
          )
        ) : selectedReport ? (
          <ReportViewer
            header={<InsightsViewerHeader name={viewerName} kind="Report" onBack={openLibrary} />}
            reportId={selectedReport}
            autoOpenSubscriptions={subscribeFor === selectedReport}
            onSubscriptionsOpened={() => setSubscribeFor(null)}
          />
        ) : (
          <EmptyState label="report" />
        )}
      </section>
      {newFolder && (
        <PromptDialog
          title="New folder"
          label="Folder name"
          placeholder="e.g. Device health"
          submitLabel="Create"
          onSubmit={async (name) => {
            const list =
              newFolder.type === 'dashboards' ? visibleDashFolders : (reportFoldersQ.data ?? []);
            const roots = list.filter((f) => f.parentId === null);
            const parentId = newFolder.parent
              ? newFolder.parent.id
              : roots.length === 1
                ? roots[0]!.id
                : null;
            await createFolder.mutateAsync({ name, type: newFolder.type, parentId });
            setNewFolder(null);
          }}
          onCancel={() => setNewFolder(null)}
        />
      )}
      {accessOpenFor && (
        <ManageAccessDialog
          dashboardId={accessOpenFor}
          dashboardName={visibleDashboards.find((d) => d.id === accessOpenFor)?.name ?? 'Dashboard'}
          onClose={closeAccess}
        />
      )}
    </div>
  );
}

/**
 * Collapsible sidebar-section header: caret + label + object-count pill.
 *
 * The entire row is one click target: clicking anywhere expands/collapses
 * the tree. When `onOpenList` is given, expanding also opens the category's
 * table in the right pane (making it the active/selected category); collapsing
 * just closes the tree. Without `onOpenList` (the Admin group) it plain toggles.
 */
function SectionHeader({
  label,
  count,
  open,
  active = false,
  onToggle,
  onOpenList,
  onNewFolder,
}: {
  label: string;
  count?: number;
  open: boolean;
  active?: boolean;
  onToggle(): void;
  onOpenList?(): void;
  /** When set (an organizer on a Reports/Dashboards section), a "new folder"
   *  action shows at the right of the header rather than inside the tree. */
  onNewFolder?: (() => void) | undefined;
}): JSX.Element {
  const caret = <ChevronRight size={12} className="embed-section-caret" />;
  const pill = count !== undefined && <span className="embed-tab-count">{count}</span>;

  // CSAGE-472: clicking a Reports/Dashboards header shows that category's
  // browsable listing in the main pane (a folder-landing-style list of its
  // objects) whenever the category is not already the active surface — so a
  // user who clicks "Dashboards" lands on the list of dashboards to pick from
  // instead of the header merely collapsing its tree and leaving the main pane
  // on the other category (the "no dashboards in the main pane" bug). Once the
  // category IS the active surface the header falls back to expand/collapse, so
  // the tree can still be hidden. Headers with no list (Admin) plain-toggle.
  const handleClick = (): void => {
    if (onOpenList) onOpenList();
    else onToggle();
  };

  return (
    <div className={'embed-section-header' + (active ? ' embed-section-header--active' : '')}>
      <button
        type="button"
        className="section-chevron"
        aria-label={`${open ? 'Collapse' : 'Expand'} ${label}`}
        aria-expanded={open}
        onClick={onToggle}
      >
        {caret}
      </button>
      <button type="button" className="section-label" onClick={handleClick}>
        <span>{label}</span>
        {pill}
      </button>
      {onNewFolder && (
        <button
          type="button"
          className="embed-section-add"
          aria-label={`New ${label.toLowerCase()} folder`}
          title="New folder"
          onClick={(e) => {
            e.stopPropagation();
            onNewFolder();
          }}
        >
          +
        </button>
      )}
    </div>
  );
}

function EmptyState({ label }: { label: string }): JSX.Element {
  return (
    <div className="empty-state" style={{ border: 'none', padding: 'var(--space-6)' }}>
      <h3>Select a {label}</h3>
      <p className="text-dim">Pick one from the list on the left.</p>
    </div>
  );
}

interface DashboardsTabBodyProps {
  dashboardsQ: ReturnType<typeof useDashboards>;
  foldersQ: ReturnType<typeof useFolders>;
  selected: string | null;
  onSelect(id: string | null): void;
  onSelectFolder(id: string | null): void;
  expanded: ReadonlySet<string>;
  onToggle(id: string): void;
  search: string;
  /** Open the in-app new-folder dialog (parent = null → top level). */
  onNewFolder(parent: Folder | null): void;
  /** CSAGE-557: open "Manage access" for a dashboard; set only when allowed. */
  onManageAccess?: ((dashboardId: string) => void) | undefined;
  /** CSAGE-586: each customer's standard boards; set for an internal admin at the org root. */
  standardByCustomer?: ReadonlyMap<string, StandardCustomerBoards> | undefined;
  onOpenStandardAsCustomer?: ((id: string, customer: StandardCustomerBoards) => void) | undefined;
  selectedStandard?: { dashboardId: string; customer: string } | null | undefined;
}
/**
 * CSAGE-396: report a failed write instead of discarding it.
 *
 * Every mutation in this nav was fired with an `onSuccess` handler and no
 * `onError`, so a rejection vanished: the user answered a prompt and the
 * tree simply never changed. That is how a 403 on `POST /folders` presented
 * as "I created a folder and it isn't showing".
 *
 * `window.alert` rather than a toast because this component already speaks
 * in `window.prompt` / `window.confirm` / `window.alert`; introducing a
 * second notification idiom here would be the inconsistent choice.
 */
type OrganizeDialogState =
  | { kind: 'rename'; title: string; initial: string; run: (name: string) => Promise<unknown> }
  | {
      kind: 'delete';
      title: string;
      message: ReactNode;
      confirmLabel: string;
      run: () => Promise<unknown>;
    }
  | {
      kind: 'move';
      title: string;
      current: string | null;
      excludeSubtreeOf?: string | undefined;
      run: (dest: string | null) => Promise<unknown>;
    }
  | null;

/**
 * Organize-action dialogs (rename / delete / move) for the embed. window.prompt
 * and window.confirm are blocked in the cross-origin embed iframe, so these use
 * the in-app PromptDialog / ConfirmDialog / MoveDialog instead. Each TabBody
 * instantiates it once, wires its mutations through the openers, and renders
 * `element`.
 */
function useOrganizeDialogs(folders: ReadonlyArray<Folder>): {
  rename: (title: string, initial: string, run: (name: string) => Promise<unknown>) => void;
  remove: (
    title: string,
    message: ReactNode,
    confirmLabel: string,
    run: () => Promise<unknown>,
  ) => void;
  move: (
    title: string,
    current: string | null,
    run: (dest: string | null) => Promise<unknown>,
    excludeSubtreeOf?: string,
  ) => void;
  element: ReactNode;
} {
  const [state, setState] = useState<OrganizeDialogState>(null);
  const close = (): void => setState(null);
  let element: ReactNode = null;
  if (state?.kind === 'rename') {
    element = (
      <PromptDialog
        title={state.title}
        label="Name"
        initialValue={state.initial}
        submitLabel="Save"
        onSubmit={async (v) => {
          await state.run(v);
          close();
        }}
        onCancel={close}
      />
    );
  } else if (state?.kind === 'delete') {
    element = (
      <ConfirmDialog
        title={state.title}
        message={state.message}
        confirmLabel={state.confirmLabel}
        onConfirm={async () => {
          await state.run();
          close();
        }}
        onCancel={close}
      />
    );
  } else if (state?.kind === 'move') {
    element = (
      <MoveDialog
        title={state.title}
        folders={folders}
        currentParentId={state.current}
        excludeSubtreeOf={state.excludeSubtreeOf}
        onSubmit={async (d) => {
          await state.run(d);
          close();
        }}
        onCancel={close}
      />
    );
  }
  return {
    rename: (title, initial, run) => setState({ kind: 'rename', title, initial, run }),
    remove: (title, message, confirmLabel, run) =>
      setState({ kind: 'delete', title, message, confirmLabel, run }),
    move: (title, current, run, excludeSubtreeOf) =>
      setState({ kind: 'move', title, current, excludeSubtreeOf, run }),
    element,
  };
}

function DashboardsTabBody(p: DashboardsTabBodyProps): JSX.Element {
  const reorder = useReorderTree();
  const renameFolder = useRenameFolder();
  const deleteFolder = useDeleteFolder();
  const moveFolder = useMoveFolder();
  const updateDashboard = useUpdateDashboardById();
  const deleteDashboard = useDeleteDashboard();
  // CSAGE-396: mirror the server's gate on POST/PATCH/DELETE /folders
  // exactly. A portal user with only INSIGHTS_VIEW maps to
  // ReportViewer + DashboardViewer and holds none of these, so every
  // organize action would 403.
  //
  // Fails OPEN when the role set is unknown. `useMyRoles` reports `[]` both
  // while GET /me/tenants is in flight and if it fails, so gating on an
  // empty list would strip an author's controls on any transient error —
  // silently removing capability, which is the same class of defect this
  // ticket exists to fix, just inverted. The server is the enforcement
  // point; this is only an affordance, so when we cannot tell, show the
  // action and let a real 403 surface through `reportWriteFailure`. A
  // genuine read-only user is never affected: they hold ReportViewer and
  // DashboardViewer, so their list is non-empty and gating applies.
  const { roles } = useMyRoles();
  const rolesKnown = roles.length > 0;
  const canOrganize =
    !rolesKnown ||
    roles.some(
      (r) =>
        r === 'ReportAuthor' ||
        r === 'ReportAdmin' ||
        r === 'DashboardAuthor' ||
        r === 'DashboardAdmin' ||
        r === 'TenantAdmin',
    );
  const folders = (p.foldersQ.data ?? []) as ReadonlyArray<Folder>;
  const dialogs = useOrganizeDialogs(folders);
  // CSAGE-583: only an internal admin renames, moves or deletes a standard board.
  const isInternalAdmin = useIsInternalAdmin();

  if (p.dashboardsQ.isLoading) return <Pad text="Loading…" />;
  if (p.dashboardsQ.error) return <Alert text="Failed to load dashboards." />;
  return (
    <>
      <DashboardFolderTree
        folders={folders}
        dashboards={p.dashboardsQ.data ?? []}
        selectedDashboardId={p.selected}
        onSelectDashboard={p.onSelect}
        onSelectFolder={p.onSelectFolder}
        expanded={p.expanded}
        onToggle={p.onToggle}
        searchQuery={p.search}
        consumerMode
        canOrganize={canOrganize}
        canOrganizeStandard={isInternalAdmin}
        standardByCustomer={p.standardByCustomer}
        onOpenStandardAsCustomer={p.onOpenStandardAsCustomer}
        selectedStandard={p.selectedStandard}
        onManageAccess={p.onManageAccess ? (d) => p.onManageAccess?.(d.id) : undefined}
        onNewFolder={p.onNewFolder}
        onRenameFolder={(f) =>
          dialogs.rename('Rename folder', f.name, (name) =>
            renameFolder.mutateAsync({ id: f.id, name }),
          )
        }
        onMoveFolder={(f) =>
          dialogs.move(
            `Move “${f.name}”`,
            f.parentId ?? null,
            (dest) => moveFolder.mutateAsync({ id: f.id, parentId: dest }),
            f.id,
          )
        }
        onDeleteFolder={(f) =>
          dialogs.remove(
            'Delete folder',
            <>
              Delete “<strong>{f.name}</strong>”? Dashboards inside move up one level.
            </>,
            'Delete',
            () => deleteFolder.mutateAsync(f.id),
          )
        }
        onNewDashboard={NOOP}
        onReorderItems={(kind, ids) =>
          reorder.mutateAsync({ kind, items: ids.map((id, i) => ({ id, sortOrder: i + 1 })) })
        }
        onReorderFolder={NOOP}
        onRenameDashboard={(d) =>
          dialogs.rename('Rename dashboard', d.name, (name) =>
            updateDashboard.mutateAsync({ id: d.id, patch: { name } }),
          )
        }
        onMoveDashboard={(d) =>
          dialogs.move(`Move “${d.name}”`, d.folderId ?? null, (dest) =>
            updateDashboard.mutateAsync({ id: d.id, patch: { folderId: dest } }),
          )
        }
        onDeleteDashboard={(d) =>
          dialogs.remove(
            'Delete dashboard',
            <>
              Delete “<strong>{d.name}</strong>”?
            </>,
            'Delete',
            () => deleteDashboard.mutateAsync(d.id),
          )
        }
        onReorderDashboard={NOOP}
      />
      {dialogs.element}
    </>
  );
}

interface ReportsTabBodyProps {
  onSelectSection?: ((folder: string, itemIds: string[]) => void) | undefined;
  reportsQ: ReturnType<typeof useReports>;
  foldersQ: ReturnType<typeof useFolders>;
  selected: string | null;
  onSelect(id: string): void;
  expanded: ReadonlySet<string>;
  onToggle(id: string): void;
  search: string;
  templatesById: undefined;
  consumerMode?: boolean;
  /** CSAGE-353: open a report straight into its Subscriptions modal. */
  onSubscribe(id: string): void;
  /** Open the in-app new-folder dialog (parent = null → top level). */
  onNewFolder(parent: Folder | null): void;
}
function ReportsTabBody(p: ReportsTabBodyProps): JSX.Element {
  const reorder = useReorderTree();
  const { roles } = useMyRoles();
  const canReorder = roles.some((role) =>
    ['ReportAuthor', 'ReportAdmin', 'DashboardAuthor', 'DashboardAdmin', 'TenantAdmin'].includes(
      role,
    ),
  );
  // Folder management for the viewer: consumers organize their reports with
  // real folder/report mutations (rename/delete folders, drag reports & folders
  // to re-file). New-folder is lifted to an in-app dialog in the parent.
  const renameFolder = useRenameFolder();
  const deleteFolder = useDeleteFolder();
  const moveFolder = useMoveFolder();
  const updateReport = useUpdateReport();
  const cloneReport = useCloneReport();
  const deleteReport = useDeleteReport();
  const folders = (p.foldersQ.data ?? []) as ReadonlyArray<Folder>;
  const dialogs = useOrganizeDialogs(folders);

  if (p.reportsQ.isLoading) return <Pad text="Loading…" />;
  if (p.reportsQ.error) return <Alert text="Failed to load reports." />;
  // Consumer (embed) view groups the per-location instances back into the ~5
  // report definitions, matching the legacy Reports module. Authoring folder
  // affordances are irrelevant here, so the full folder tree is only used in
  // the (non-consumer) standalone path.
  if (p.consumerMode ?? false) {
    return (
      <>
        <ConsumerReportsList
          onSelectSection={p.onSelectSection}
          onReorderItems={
            canReorder
              ? (ids) =>
                  reorder.mutateAsync({
                    kind: 'reports',
                    items: ids.map((id, i) => ({ id, sortOrder: i + 1 })),
                  })
              : undefined
          }
          reports={p.reportsQ.data ?? []}
          selectedReportId={p.selected}
          onSelectReport={p.onSelect}
          searchQuery={p.search}
          /* CSAGE-353 / CSAGE-534: curate a saved copy via the in-app dialogs
             (native prompt/confirm are blocked in the embed iframe). */
          onRenameReport={(r) =>
            dialogs.rename('Rename report', r.name, (name) =>
              updateReport.mutateAsync({ id: r.id, patch: { name } }),
            )
          }
          onDeleteReport={(r) =>
            dialogs.remove(
              'Delete report',
              <>
                Delete “<strong>{r.name}</strong>”? This cannot be undone.
              </>,
              'Delete',
              () => deleteReport.mutateAsync(r.id),
            )
          }
          onSubscribeReport={(r) => p.onSubscribe(r.id)}
        />
        {dialogs.element}
      </>
    );
  }
  return (
    <>
      <ReportsFolderTree
        folders={folders}
        reports={p.reportsQ.data ?? []}
        selectedReportId={p.selected}
        onSelectReport={p.onSelect}
        expanded={p.expanded}
        onToggle={p.onToggle}
        searchQuery={p.search}
        consumerMode={p.consumerMode ?? false}
        onNewFolder={p.onNewFolder}
        onRenameFolder={(f) =>
          dialogs.rename('Rename folder', f.name, (name) =>
            renameFolder.mutateAsync({ id: f.id, name }),
          )
        }
        onDeleteFolder={(f) =>
          dialogs.remove(
            'Delete folder',
            <>
              Delete “<strong>{f.name}</strong>”? Reports inside move up one level.
            </>,
            'Delete',
            () => deleteFolder.mutateAsync(f.id),
          )
        }
        onMoveFolder={NOOP}
        onReorderFolder={NOOP}
        onRenameReport={(r) =>
          dialogs.rename('Rename report', r.name, (name) =>
            updateReport.mutateAsync({ id: r.id, patch: { name } }),
          )
        }
        onCloneReport={(r) =>
          dialogs.rename('Duplicate report as', `${r.name} (copy)`, (name) =>
            cloneReport.mutateAsync({ id: r.id, name }),
          )
        }
        onDeleteReport={(r) =>
          dialogs.remove(
            'Delete report',
            <>
              Delete “<strong>{r.name}</strong>”?
            </>,
            'Delete',
            () => deleteReport.mutateAsync(r.id),
          )
        }
        onMoveReport={NOOP}
        onRunReport={(r) => p.onSelect(r.id)}
        onReorderReport={NOOP}
        onDropReportToFolder={(reportId, folderId) =>
          updateReport.mutate({ id: reportId, patch: { folderId } })
        }
        onDropFolderToFolder={(folderId, parentId) => moveFolder.mutate({ id: folderId, parentId })}
      />
      {dialogs.element}
    </>
  );
}

function Pad({ text }: { text: string }): JSX.Element {
  return (
    <div className="text-dim text-sm" style={{ padding: 'var(--space-3)' }}>
      {text}
    </div>
  );
}
function Alert({ text }: { text: string }): JSX.Element {
  return (
    <div className="alert alert--danger" style={{ margin: 'var(--space-3)' }}>
      {text}
    </div>
  );
}
